import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { StateService } from '@uirouter/angular';
import * as _ from 'lodash';
import { EventWebhookService, EventDelivery, EventSubscription } from '../../core/services/core.event-webhook.service';
import { FilterService } from '../../core/services/core.filter.service';

@Component({
    selector: 'event-delivery-list',
    template: require('./event-delivery-list.component.html'),
})
export class EventDeliveryListComponent implements OnInit, OnChanges {
    @Input() subscriptionId: number | null = null;
    @Input() projectId: number | null = null;

    selectedDeliveryId: number | null = null;
    deliveries: EventDelivery[] = [];
    subscriptions: EventSubscription[] = [];
    loading = false;
    error: string | null = null;

    sort: any = {};
    paging: any = {};
    filters: any = {};
    sortFields = {
        id: 'ID',
        date: 'Date',
        subscription_name: 'Subscription',
        event_type: 'Event Type',
        status: 'Status',
    };

    constructor(
        private service: EventWebhookService,
        private filterService: FilterService,
        private stateService: StateService,
    ) {}

    ngOnInit() {
        this.sort = this.filterService.getSortValues('date', 'desc');
        this.paging = this.filterService.getPagingValues();

        this.loadSubscriptions().then(() => {
            this.setFilterValues();
            this.load();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.subscriptionId && !changes.subscriptionId.firstChange) {
            this.load();
        }
    }

    loadSubscriptions(): Promise<void> {
        // page_size raised well past the default 20 so the dropdown includes
        // every subscription in scope, not just the first page.
        const filters: any = { page_size: 1000 };
        if (this.projectId !== null) { filters.project_id = this.projectId; }

        return this.service.listSubscriptions(filters)
            .then(subs => { this.subscriptions = (subs && subs.items) ? subs.items : []; })
            .catch(() => { /* non-fatal — filter just stays empty */ });
    }

    getBaseFilters(): any[] {
        const any = this.filterService.getAnyFilterObj();
        const fields: any[] = [];

        if (this.subscriptionId === null) {
            fields.push({
                alias: 'subscription_id',
                fieldName: 'Subscription Name',
                value: any,
                fieldType: 1,
                isExtraField: false,
                fieldElements: ([any] as any[]).concat(_.map(this.subscriptions, (s: EventSubscription) => (
                    { id: s.id, label: s.name || s.event_pattern }
                ))),
            });
        }

        fields.push(
            { alias: 'event_type', fieldName: 'Event Type', value: '', fieldType: 4, isExtraField: false },
            {
                alias: 'status', fieldName: 'Status', value: any, fieldType: 1, isExtraField: false,
                fieldElements: [
                    any,
                    { id: 'pending', label: 'pending' },
                    { id: 'in_flight', label: 'in_flight' },
                    { id: 'success', label: 'success' },
                    { id: 'failed', label: 'failed' },
                    { id: 'dead', label: 'dead' },
                ],
            },
            { alias: 'date_start', fieldName: 'Start Date', value: '', fieldType: 9, isExtraField: false },
            { alias: 'date_end', fieldName: 'End Date', value: '', fieldType: 9, isExtraField: false },
        );

        return fields;
    }

    setFilterValues() {
        this.filters = {
            main_fields: this.filterService.setFilterValues(this.getBaseFilters(), this.stateService.params),
        };
    }

    load() {
        this.loading = true;
        this.error = null;

        const filters: any = Object.assign(
            {},
            this.sort,
            this.paging,
            this.filterService.getFilterValues(_.get(this.filters, 'main_fields', [])),
        );

        // A fixed @Input always wins over whatever the filter panel holds.
        if (this.subscriptionId !== null) { filters.subscription_id = this.subscriptionId; }
        if (this.projectId !== null) { filters.project_id = this.projectId; }

        this.service.listDeliveries(filters)
            .then(rows => {
                this.deliveries = (rows && rows.items) ? rows.items : [];
                this.loading = false;
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Failed to load deliveries';
                this.loading = false;
            });
    }

    onApplyFilters = () => {
        this.paging.page_num = 1;
        this.applyFilters();
    };

    onResetFilters = () => {
        this.paging.page_num = 1;
        this.filters = { main_fields: this.getBaseFilters() };
        this.applyFilters();
    };

    onSort = (params: any) => {
        this.sort = {
            sort_field: _.get(params, 'key', 'date'),
            sort_dir: _.get(params, 'sortDir', 'desc'),
        };
        this.applyFilters();
    };

    onPaging = (params: any) => {
        _.assignIn(this.paging, params);
        this.applyFilters();
    };

    applyFilters(overrides = {}) {
        const newParams = _.pickBy(Object.assign(
            // `inherit: false` below drops ancestor path params — when this widget is
            // embedded on the project-admin page, `home.project.summary`'s required
            // `:unixName` must be carried forward explicitly or the transition is
            // rejected as invalid before any request is made. Harmless no-op on the
            // site-admin page, where unixName is undefined and pickBy strips it.
            { unixName: this.stateService.params.unixName },
            this.sort,
            this.paging,
            this.filterService.getFilterValues(_.get(this.filters, 'main_fields', [])),
            overrides,
        ), (value) => (undefined !== value && null !== value && value !== ''));

        this.stateService.go('.', newParams, { inherit: false }).then(() => this.load());
    }

    subscriptionName(id: number | string | null): string {
        if (id === null || id === undefined || id === '') { return '—'; }
        const sub: any = _.find(this.subscriptions, (s: EventSubscription) => s.id == id);
        return sub ? (sub.name || sub.event_pattern) : String(id);
    }

    statusClass(status: string): string {
        if (status === 'success')                     { return 'label-success'; }
        if (status === 'failed' || status === 'dead') { return 'label-danger'; }
        if (status === 'pending' || status === 'in_flight') { return 'label-warning'; }
        return 'label-default';
    }

    onSelect(id: number) {
        this.selectedDeliveryId = (this.selectedDeliveryId === id) ? null : id;
    }
}
